import React, { useCallback, useEffect, useReducer } from 'react';
import * as Sentry from '@sentry/react';
import { identifyUser as analyticsIdentify, resetAnalytics } from '@/services/analytics';
import queryClient from '@/queryClient';
import { AuthContext } from '../context/AuthContext';
import { logoutSession } from '../services';
import type { AuthContextValue, AuthState, User } from '../types';
import {
  clearAuthStorage,
  getAccessToken,
  getRefreshToken,
  getStoredUser,
  isTokenExpired,
  setAccessToken as persistAccessToken,
  setAuthStorage,
  setStoredUser,
} from '../utils/tokenStorage';

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN'; payload: { user: User; accessToken: string; refreshToken: string } }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'UPDATE_ACCESS_TOKEN'; payload: string };

function identifyUser(user: User) {
  Sentry.setUser({ id: String(user.id), email: user.email, username: user.username });
  analyticsIdentify(String(user.id), { email: user.email, username: user.username });
}

function getInitialAuthState(): AuthState {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  const user = getStoredUser();

  if (!accessToken || !refreshToken || !user) {
    return { user: null, accessToken: null, refreshToken: null, isAuthenticated: false, isLoading: false };
  }

  if (isTokenExpired(accessToken)) {
    clearAuthStorage();
    return { user: null, accessToken: null, refreshToken: null, isAuthenticated: false, isLoading: false };
  }

  identifyUser(user);
  return { user, accessToken, refreshToken, isAuthenticated: true, isLoading: false };
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'LOGIN':
      return {
        ...state,
        user: action.payload.user,
        accessToken: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'UPDATE_USER':
      return { ...state, user: action.payload };
    case 'UPDATE_ACCESS_TOKEN':
      return { ...state, accessToken: action.payload };
    default:
      return state;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, null, getInitialAuthState);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== 'accessToken') return;

      if (!e.newValue) {
        Sentry.setUser(null);
        dispatch({ type: 'LOGOUT' });
        return;
      }

      if (e.oldValue !== e.newValue) {
        const user = getStoredUser();
        const refreshToken = getRefreshToken();
        if (user && refreshToken) {
          dispatch({ type: 'LOGIN', payload: { user, accessToken: e.newValue, refreshToken } });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const handleAuthLogout = () => dispatch({ type: 'LOGOUT' });
    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, []);

  const login = useCallback((accessToken: string, refreshToken: string, user: User) => {
    setAuthStorage(accessToken, refreshToken, user);
    identifyUser(user);
    dispatch({ type: 'LOGIN', payload: { user, accessToken, refreshToken } });
  }, []);

  const logout = useCallback(async () => {
    await logoutSession();
    clearAuthStorage();
    Sentry.setUser(null);
    resetAnalytics();
    queryClient.clear();
    dispatch({ type: 'LOGOUT' });
  }, []);

  const updateUser = useCallback((user: User) => {
    setStoredUser(user);
    identifyUser(user);
    dispatch({ type: 'UPDATE_USER', payload: user });
  }, []);

  const updateAccessToken = useCallback((accessToken: string) => {
    persistAccessToken(accessToken);
    dispatch({ type: 'UPDATE_ACCESS_TOKEN', payload: accessToken });
  }, []);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    updateUser,
    updateAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
